/**
 * @file    useLineChartSettings
 * @scope   SHARED
 * @summary Owns the line-chart toggle and the goal-line / Y-axis settings derived from options.
 * @consumes WidgetContext, widget options
 * @produces { showLineChart, setShowLineChart, yAxisValues, goalValues, drawGoalLine }
 * @sideEffects Publishes `showLineChart` and the SWITCH_BAR_LINE default to the widget context.
 */
import { useEffect, useState } from 'react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { GoalValues } from '../types';

export const useLineChartSettings = (options: any) => {
  const { defaults, setState } = useWidgetContext();
  const {
    goal,
    optYAxisValues,
    setYAxisMinMaxValues: optSetYAxisMinMaxValues,
    drawGoalLine: optDrawGoalLine,
  } = options || {};

  const [showLineChart, setShowLineChart] = useState<boolean>(options?.showLineChart ?? false);
  const [yAxisValues, setYAxisValues] = useState<any>({});
  const [drawGoalLine, setDrawGoalLine] = useState<boolean>(false);
  const [goalValues, setGoalValues] = useState<GoalValues>({});

  useEffect(() => {
    const isLineChartEnabled = options?.showLineChart ?? false;
    setShowLineChart(isLineChartEnabled);
    setState({
      showLineChart: isLineChartEnabled,
      defaults: { ...defaults, SWITCH_BAR_LINE: isLineChartEnabled ? 'line' : 'bars' },
    });
  }, [options?.showLineChart]);

  useEffect(() => {
    if (goal) setGoalValues(goal);
  }, [goal]);

  useEffect(() => {
    if (optYAxisValues) setYAxisValues(optYAxisValues);
  }, [optYAxisValues]);

  useEffect(() => {
    if (options && 'optYAxisValues' in options && optSetYAxisMinMaxValues) {
      setYAxisValues(optYAxisValues);
    } else {
      setYAxisValues({});
    }
  }, [optSetYAxisMinMaxValues]);

  useEffect(() => {
    if (options && 'drawGoalLine' in options) {
      // QUIRK: turning the flag off clears the goal values but never resets drawGoalLine.
      optDrawGoalLine ? setDrawGoalLine(optDrawGoalLine) : setGoalValues({});
    }
  }, [optDrawGoalLine]);

  return { showLineChart, setShowLineChart, yAxisValues, goalValues, drawGoalLine };
};
